import { useState } from 'react';
import { Play, X } from 'lucide-react';
import type { ComponentTestInput, WorkflowTestInputs } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { SchedulingScopeFields } from '@/scheduling/components/SchedulingScopeFields';
import { useSchedulingScope, type SchedulingScope } from '@/scheduling/hooks/use-scheduling-scope';
import { has, spec, testInputsBody } from './test-call';

/**
 * What a test asks the tester for before the call starts (ADR-306).
 *
 * THE STEP DECIDES WHAT IS ASKED. Each component declares its test inputs in
 * the catalog (`testInputs`); this renders a picker for each kind it named and
 * nothing else. The provider, office and visit-type choices are the task
 * drawer's own (`useSchedulingScope`), so a tester picks from exactly the list
 * an operator scheduling a real call picks from.
 *
 * Nothing picked here is written anywhere: the API checks every id belongs to
 * this practice and hands the picks to the call, and the made-up appointment
 * exists only in that call.
 */

/** Undo a group's picks: a Select cannot be emptied once chosen. */
function ClearButton({ onClear }: { onClear: () => void }): React.ReactElement {
  return (
    <Button type="button" variant="ghost" size="sm" className="self-end gap-1" onClick={onClear}>
      <X className="h-3.5 w-3.5" />
      Clear
    </Button>
  );
}

/**
 * Why a provider cannot be used: the office picker is hidden when there is
 * nothing to pick, so without this the sheet only says to pick an office.
 */
function NoOfficeWarning({ scope }: { scope: SchedulingScope }): React.ReactElement | null {
  const none =
    !!scope.providerId && !scope.providerLocationsLoading && scope.locationOptions.length === 0;
  return none ? (
    <p className="text-xs text-destructive">
      This provider has no office linked. Link one in Providers settings, or pick another.
    </p>
  ) : null;
}

export function TestInputsForm({
  inputs,
  onStart,
}: {
  inputs: readonly ComponentTestInput[];
  onStart: (body: WorkflowTestInputs) => void;
}): React.ReactElement {
  const scope = useSchedulingScope();
  const existing = useSchedulingScope();
  const [at, setAt] = useState('');

  const verdict = testInputsBody(inputs, {
    providerId: scope.providerId,
    locationId: scope.locationId,
    appointmentTypeId: scope.appointmentTypeId,
    existing: {
      providerId: existing.providerId,
      locationId: existing.locationId,
      appointmentTypeId: existing.appointmentTypeId,
      at,
    },
  });
  const existingSpec = spec(inputs, 'existingAppointment');
  const wantsScope = has(inputs, 'provider') || has(inputs, 'appointmentType');

  return (
    <>
      <SheetBody className="pt-4">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            This step uses a few details a real call gets from the task. Pick them for the test.
            Nothing is booked, sent or changed: the call records what it would have done.
          </p>

          {wantsScope && (
            <div className="flex flex-col gap-4">
              <ClearButton
                onClear={() => {
                  scope.setProviderId('');
                  scope.setLocationId('');
                  scope.setAppointmentTypeId('');
                }}
              />
              <SchedulingScopeFields scope={scope} idPrefix="test-scope" />
              <NoOfficeWarning scope={scope} />
              {[spec(inputs, 'provider'), spec(inputs, 'appointmentType')]
                .filter((s): s is ComponentTestInput => !!s?.help)
                .map((s) => (
                  <p key={s.kind} className="text-xs text-muted-foreground">
                    {s.label}: {s.help}
                  </p>
                ))}
            </div>
          )}

          {existingSpec && (
            <div className="flex flex-col gap-4 rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">
                  {existingSpec.label}
                  {existingSpec.required ? '' : ' (optional)'}
                </p>
                {existingSpec.help && (
                  <p className="text-xs text-muted-foreground">{existingSpec.help}</p>
                )}
              </div>
              <ClearButton
                onClear={() => {
                  existing.setProviderId('');
                  existing.setLocationId('');
                  existing.setAppointmentTypeId('');
                  setAt('');
                }}
              />
              <SchedulingScopeFields
                scope={existing}
                idPrefix="test-existing"
                providerHint={null}
              />
              <NoOfficeWarning scope={existing} />
              <div className="flex flex-col gap-2">
                <Label htmlFor="test-existing-at">Date and time, at the office</Label>
                <Input
                  id="test-existing-at"
                  type="datetime-local"
                  value={at}
                  onChange={(e) => setAt(e.target.value)}
                />
              </div>
            </div>
          )}

          {'missing' in verdict && (
            <p className="text-xs text-muted-foreground">{verdict.missing}</p>
          )}
        </div>
      </SheetBody>
      <SheetActions>
        <SheetCancel />
        <Button
          className="gap-2"
          disabled={'missing' in verdict}
          onClick={() => 'body' in verdict && onStart(verdict.body)}
        >
          <Play className="h-4 w-4" />
          Start the test call
        </Button>
      </SheetActions>
    </>
  );
}
